import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DoneIcon from "@mui/icons-material/Done";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useWebServer } from "../../../../providers";
import usePolling from "../../../../lib/usePollingHook";
import {
  fetchActivityAlerts,
  setActivityAlertType,
  markActivityAlertsRead,
} from "../../../../actions";
import { environmentMapper } from "../../../../utils/environment";
import {
  ACTIVITY_REFRESH_INTERVAL_MS,
  ACTIVITY_MAX_REFRESH_ROWS,
} from "./constants";
import { formatLastCapture, formatTimestamp } from "./format";

const fontSx = {
  fontSize: ["0.75rem", "0.75rem", "0.75rem", "0.75rem", "1rem", "1rem"],
};

// Amber means one thing across the Activity tab: a lifecycle condition that is true
// right now, from the row's healthWarnings. This list is alert history, read or not,
// so it keeps red for maintenance and stays neutral for lifecycle rather than reuse
// amber for "an alert of that type was once raised".
const alertBorderColor = (alert) =>
  alert?.alertType === "maintenance" ? "status.danger" : "grey.400";

const AlertMarker = ({ alert }) =>
  alert?.alertType === "maintenance" ? (
    <PriorityHighIcon
      fontSize="small"
      sx={{ color: "status.danger", verticalAlign: "middle", mr: "0.25rem" }}
    />
  ) : null;

// the same healthWarnings that light the fleet row's triangle, so opening the device
// always shows why the row is amber - whichever alert tab is selected, and whether or
// not the alerts behind it have been read
const HealthWarnings = ({ warnings }) =>
  warnings?.length ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "0.5rem",
        mb: "1rem",
        p: "0.75rem",
        border: "1px solid",
        borderColor: "status.warning",
        borderRadius: "0.5rem",
      }}
    >
      <WarningAmberIcon sx={{ color: "status.warning" }} />
      <Box>
        {warnings.map((warning) => (
          <Typography key={warning.code} sx={fontSx}>
            {warning.message}
          </Typography>
        ))}
      </Box>
    </Box>
  ) : null;

export const deriveAlertSummary = (alert) => {
  if (alert?.message) {
    return alert.message;
  }
  if (alert?.kind === "shadow") {
    const keys = Object.keys(alert?.raw || {});
    return keys.length > 0 ? `shadow: ${keys.join(", ")}` : "shadow";
  }
  return alert?.kind || "-";
};

const SpecCell = ({ label, value }) => (
  <Grid size={4}>
    <Typography sx={fontSx} className={"text-gray-600"}>
      {label}
    </Typography>
    <Typography sx={fontSx}>{value ?? "-"}</Typography>
  </Grid>
);

const Index = ({
  device,
  onClose,
  alertType,
  alerts = [],
  alertsCursor,
  alertsLoading,
  alertsError,
  fetchActivityAlerts,
  setActivityAlertType,
  markActivityAlertsRead,
}) => {
  const { sendOrganizationRequest } = useWebServer();

  const refreshingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const deviceId = device?.deviceId;

  useEffect(() => {
    if (deviceId) {
      fetchActivityAlerts(sendOrganizationRequest, deviceId, {
        alertType,
      });
    }
  }, [deviceId, alertType, sendOrganizationRequest, fetchActivityAlerts]);

  const refreshRef = useRef({});
  useEffect(() => {
    refreshRef.current = {
      sendOrganizationRequest,
      alertType,
      loadedAlerts: alerts.length,
    };
  });

  usePolling(
    () => {
      const {
        sendOrganizationRequest: send,
        alertType: currentAlertType,
        loadedAlerts,
      } = refreshRef.current;
      // see ActivityMonitor: a refresh re-requests every page already loaded, so
      // paging no longer parks the poll for good
      if (
        refreshingRef.current ||
        loadingMoreRef.current ||
        loadedAlerts > ACTIVITY_MAX_REFRESH_ROWS
      ) {
        return;
      }
      refreshingRef.current = true;
      fetchActivityAlerts(send, deviceId, {
        alertType: currentAlertType,
        limit: loadedAlerts || undefined,
        background: true,
      }).finally(() => {
        refreshingRef.current = false;
      });
    },
    ACTIVITY_REFRESH_INTERVAL_MS,
    !!deviceId
  );

  if (!device) {
    return null;
  }

  const desired = device.desired || {};
  const reported = device.reported || {};

  const handleLoadMore = () => {
    if (loadingMoreRef.current || alertsLoading || !alertsCursor) {
      return;
    }
    loadingMoreRef.current = true;
    fetchActivityAlerts(sendOrganizationRequest, device.deviceId, {
      alertType,
      cursor: alertsCursor,
    }).finally(() => {
      loadingMoreRef.current = false;
    });
  };

  const handleMarkRead = (alert) => {
    markActivityAlertsRead(
      sendOrganizationRequest,
      device.deviceId,
      { ids: [alert._id] },
      { organizationId: device.organizationId, alertType: alert.alertType }
    );
  };

  // "Mark all read" is a server-side operation over the whole device, so gating it
  // on the alerts currently paged in would disable it whenever the unread ones sit
  // on a later page. The fleet row carries an authoritative maintenance count;
  // lifecycle has no equivalent, so that tab falls back to what is loaded.
  const hasUnread =
    alertType === "maintenance"
      ? device.unreadMaintenanceCount > 0
      : alerts.some((alert) => !alert.read);

  const handleMarkAllRead = () => {
    markActivityAlertsRead(
      sendOrganizationRequest,
      device.deviceId,
      { all: true, alertType },
      { organizationId: device.organizationId, alertType }
    );
  };

  return (
    <Dialog open={!!device} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
          <Typography component="span" sx={{ fontSize: "1.25rem" }}>
            {device.deviceId}
          </Typography>
          <Typography component="span" sx={fontSx} className={"text-gray-600"}>
            {device.displayName}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <HealthWarnings warnings={device.healthWarnings} />
        <Grid container spacing={2}>
          <SpecCell
            label="State machine"
            value={desired.state_machine ?? reported.state_machine}
          />
          <SpecCell
            label="Last capture"
            value={formatLastCapture(device.lastCaptureAt)}
          />
          <SpecCell
            label="Last alert"
            value={formatTimestamp(device.last_alert_timestamp)}
          />
          <SpecCell
            label="Firmware"
            value={desired.firmware_version ?? reported.firmware_version}
          />
          <SpecCell
            label="Algorithm / Region"
            value={
              desired.alg_version ??
              reported.alg_version ??
              desired.vector_type ??
              reported.vector_type
            }
          />
          <SpecCell
            label="Environment"
            value={environmentMapper(desired.env ?? reported.env) || null}
          />
          <SpecCell label="Hardware ID" value={device.hardwareId} />
          <SpecCell label="Display name" value={device.displayName} />
          <SpecCell
            label="Organization"
            value={
              device.organizationId === "__unassigned__"
                ? "No organization"
                : device.organizationName
            }
          />
        </Grid>

        <Box sx={{ mt: "1.5rem" }}>
          <ToggleButtonGroup
            value={alertType}
            exclusive
            size="small"
            onChange={(e, value) => {
              if (value !== null) {
                setActivityAlertType(value);
              }
            }}
          >
            <ToggleButton value="maintenance">Maintenance</ToggleButton>
            <ToggleButton value="lifecycle">Lifecycle</ToggleButton>
          </ToggleButtonGroup>

          <List>
            {alerts.length === 0 ? (
              alertsLoading ? (
                <CircularProgress size="1.5rem" sx={{ my: "1rem" }} />
              ) : (
                <Typography sx={fontSx} className={"text-gray-600"}>
                  {alertsError
                    ? "Could not load alerts. Retry in a moment."
                    : "No alerts"}
                </Typography>
              )
            ) : (
              alerts.map((alert) => (
                <ListItem
                  key={alert._id}
                  sx={{
                    borderLeft: alert.read ? "none" : "4px solid",
                    borderColor: alertBorderColor(alert),
                    fontWeight: alert.read ? "normal" : "bold",
                    color: alert.read ? "text.secondary" : "text.primary",
                  }}
                  secondaryAction={
                    !alert.read ? (
                      <Tooltip title="Mark read">
                        <IconButton onClick={() => handleMarkRead(alert)}>
                          <DoneIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null
                  }
                >
                  <Box>
                    <Typography sx={fontSx} component="span">
                      <AlertMarker alert={alert} />
                      {formatTimestamp(alert.timestamp)} · {alert.alertType}
                      {"  "}
                      <Chip
                        size="small"
                        label={alert.read ? "Read" : "Unread"}
                        color={alert.read ? "default" : "error"}
                      />
                    </Typography>
                    <Typography
                      sx={{
                        ...fontSx,
                        fontWeight: alert.read ? "normal" : "bold",
                      }}
                    >
                      {deriveAlertSummary(alert)}
                    </Typography>
                  </Box>
                </ListItem>
              ))
            )}
          </List>

          {alertsCursor ? (
            <Button onClick={handleLoadMore} disabled={alertsLoading}>
              Load more
            </Button>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleMarkAllRead} disabled={!hasUnread}>
          Mark all read
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  const { alertType, alerts, alertsCursor, alertsLoading, alertsError } =
    state?.activityMonitor || {};
  return { alertType, alerts, alertsCursor, alertsLoading, alertsError };
};

export default connect(mapStateToProps, {
  fetchActivityAlerts,
  setActivityAlertType,
  markActivityAlertsRead,
})(Index);
